/**
 * Formats (ratios) proposés à la génération d'images, et compatibilité par modèle.
 *
 * Table de présentation : elle décide de ce que l'utilisateur peut sélectionner.
 * La validation fait autorité côté serveur (`server/src/config/formatsImage.js`),
 * qui porte en plus les résolutions explicites attendues par certains fournisseurs.
 * Toute modification de l'une doit être répercutée dans l'autre.
 */

// Identifiants des modèles de génération d'images
export const MODELE_NANO_FAST = 'nano-banana-2-fast';
export const MODELE_NANO_2 = 'nano-banana-2';
export const MODELE_NANO_PRO = 'nano-banana-pro';
export const MODELE_GPT = 'openai/gpt-image-2.5/sunburst/text-to-image';

// Modèles proposés dans les écrans de génération
export const MODELES_IMAGE = [
  { value: MODELE_NANO_FAST, label: 'NanoBanana 2 Fast — rapide, économique' },
  { value: MODELE_NANO_2, label: 'NanoBanana 2 — équilibré' },
  { value: MODELE_NANO_PRO, label: 'NanoBanana Pro — meilleure qualité' },
  { value: MODELE_GPT, label: 'GPT Image 2.5 Sunburst — respect du prompt' }
];

/**
 * Un format est accepté par un modèle si celui-ci figure dans `modeles`.
 * `valeur` est le ratio numérique, utilisé pour retomber sur le format le plus
 * proche quand un changement de modèle rend la sélection courante indisponible.
 */
export const FORMATS_IMAGE = {
  '1:1': {
    label: 'Carré (1:1 — 1024 × 1024)',
    valeur: 1,
    modeles: [MODELE_NANO_FAST, MODELE_NANO_2, MODELE_NANO_PRO, MODELE_GPT]
  },
  '4:5': {
    label: 'Portrait Meta (4:5) — format du fil',
    valeur: 0.8,
    modeles: [MODELE_NANO_FAST, MODELE_NANO_2, MODELE_NANO_PRO, MODELE_GPT]
  },
  '2:3': {
    label: 'Portrait (2:3 — 1024 × 1536)',
    valeur: 2 / 3,
    modeles: [MODELE_NANO_FAST, MODELE_NANO_2, MODELE_NANO_PRO, MODELE_GPT]
  },
  '9:16': {
    label: 'Vertical (9:16 — story et reel)',
    valeur: 9 / 16,
    modeles: [MODELE_NANO_FAST, MODELE_NANO_2, MODELE_NANO_PRO, MODELE_GPT]
  }
};

// Ratio appliqué par défaut
export const FORMAT_PAR_DEFAUT = '1:1';

/**
 * Options de format acceptées par un modèle, prêtes pour un Select.
 *
 * @param {string} model - Identifiant du modèle sélectionné
 * @returns {Array<{ value: string, label: string }>}
 */
export const formatsPourModele = (model) =>
  Object.entries(FORMATS_IMAGE)
    .filter(([, format]) => format.modeles.includes(model))
    .map(([value, format]) => ({ value, label: format.label }));

/**
 * Ratio à retenir après un changement de modèle.
 * Conserve la sélection si le nouveau modèle l'accepte, sinon retombe sur le
 * format supporté dont le ratio est le plus proche.
 *
 * @param {string} ratioCourant
 * @param {string} model - Nouveau modèle sélectionné
 * @returns {string} Ratio à appliquer
 */
export const formatCompatible = (ratioCourant, model) => {
  const disponibles = formatsPourModele(model);
  if (disponibles.length === 0) return ratioCourant;
  if (disponibles.some(format => format.value === ratioCourant)) return ratioCourant;

  const cible = FORMATS_IMAGE[ratioCourant]?.valeur ?? FORMATS_IMAGE[FORMAT_PAR_DEFAUT].valeur;

  return disponibles.reduce((meilleur, format) => {
    const ecartCourant = Math.abs(FORMATS_IMAGE[format.value].valeur - cible);
    const ecartMeilleur = Math.abs(FORMATS_IMAGE[meilleur].valeur - cible);
    return ecartCourant < ecartMeilleur ? format.value : meilleur;
  }, disponibles[0].value);
};
